// @ts-nocheck
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, RotateCcw, Search, X } from 'lucide-react';
import { useCompanyContext } from '../context/CompanyContext';
import { ClientPageLede } from '../components/WorkflowFormPrimitives';
import { ClientEmptyStateCard } from '../../shared/ClientEmptyStateCard';
import { clientDataPanelClass } from '../../../utils/clientChromeClasses';

export default function ArchivedPage() {
  const { archivedAnalyses, restoreAnalysis } = useCompanyContext();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [restoreTarget, setRestoreTarget] = useState(null);

  const searchLower = (searchQuery || '').trim().toLowerCase();
  const filteredArchived = searchLower
    ? archivedAnalyses.filter((a) => {
        const name = (a.name || '').toLowerCase();
        const company = (a.data?.companyName || '').toLowerCase();
        const closing = (a.data?.closingDate || '').toLowerCase();
        const reason = String(a.deleteReason || a.archivedReason || '').toLowerCase();
        return (
          name.includes(searchLower) ||
          company.includes(searchLower) ||
          closing.includes(searchLower) ||
          reason.includes(searchLower)
        );
      })
    : archivedAnalyses;

  const formatArchiveReason = (a) => {
    const r = String(a.deleteReason || a.archivedReason || '').trim();
    return r.length > 0 ? r : '—';
  };

  const openRestoreDialog = (analysis) => {
    setRestoreTarget(analysis);
  };

  const closeRestoreDialog = () => {
    setRestoreTarget(null);
  };

  const confirmRestore = () => {
    if (!restoreTarget) return;
    restoreAnalysis(restoreTarget.id);
    setRestoreTarget(null);
    navigate('/client/analyses');
  };

  return (
    <div className="space-y-3">
      <ClientPageLede>
        <p>
          Analyses you remove from <strong className="font-semibold text-gray-700">280G Analyses</strong> appear here.
          Search by name, company, closing date, or archive reason. Restore returns an analysis to the active list.
        </p>
      </ClientPageLede>

      {archivedAnalyses.length === 0 ? (
        <ClientEmptyStateCard icon={Archive} title="No archived analyses.">
          <p>Analyses you delete from 280G Analyses will appear here.</p>
        </ClientEmptyStateCard>
      ) : (
        <div className={clientDataPanelClass}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-5 py-3 border-b border-gray-200 bg-gray-50/80">
            <div className="relative w-full sm:flex-1 sm:min-w-0 sm:max-w-md">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                aria-hidden
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, company, closing date, or reason…"
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white placeholder:text-gray-400 focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
                aria-label="Search archived analyses"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Analysis
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Closing Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reason
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredArchived.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                      No archived analyses match your search.
                    </td>
                  </tr>
                ) : (
                  filteredArchived.map((a) => {
                    const reasonText = formatArchiveReason(a);
                    return (
                      <tr key={a.id} className="hover:bg-gray-50/50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{a.name || 'Untitled'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{a.data?.companyName || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{a.data?.closingDate || '—'}</td>
                        <td
                          className="px-4 py-3 text-sm text-gray-600 max-w-[280px]"
                          title={reasonText === '—' ? undefined : reasonText}
                        >
                          <span className="line-clamp-2 break-words">{reasonText}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => openRestoreDialog(a)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          >
                            <RotateCcw size={12} /> Restore
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {restoreTarget && (
        <>
          <div
            className="fixed inset-0 bg-black/20 z-40 transition-opacity"
            onClick={closeRestoreDialog}
            aria-hidden="true"
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="w-full max-w-md bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-900">Restore Analysis</h3>
                <button
                  type="button"
                  onClick={closeRestoreDialog}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="px-5 py-4 space-y-3">
                <p className="text-sm text-gray-600">
                  This analysis will be moved back to your active 280G Analyses list.
                </p>
                <div className="py-2 px-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Analysis</p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {restoreTarget.name || 'Untitled analysis'}
                  </p>
                  <p className="text-xs text-gray-500">{restoreTarget.data?.companyName || 'No company name'}</p>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeRestoreDialog}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmRestore}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 transition-colors"
                >
                  Restore
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
